import React from "react";
import styled from "styled-components";

// Styled components to match company dashboard aesthetic
const DetailsContainer = styled.div`
  padding: 1.5rem;
  color: rgba(255, 255, 255, 0.9);
  animation: fadeIn 0.5s ease-out;
  background: linear-gradient(
    135deg,
    rgba(15, 23, 42, 0.8) 0%,
    rgba(31, 41, 55, 0.9) 100%
  );
  border-radius: 16px;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const DetailsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 1rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100px;
    height: 1px;
    background: linear-gradient(90deg, rgba(99, 102, 241, 0.8), transparent);
  }
`;

const CloseButton = styled.button`
  background-color: transparent;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.5rem;
  padding: 0.25rem 0.5rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: rgba(255, 255, 255, 1);
    transform: scale(1.1);
  }
`;

const DetailsTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
`;

const DetailsCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    box-shadow: 0 8px 32px rgba(31, 38, 135, 0.15);
    transform: translateY(-2px);
  }

  h3 {
    margin-top: 0;
    margin-bottom: 1rem;
    font-size: 1.25rem;
    color: rgba(255, 255, 255, 0.95);
    position: relative;
    padding-bottom: 0.75rem;
    
    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 40px;
      height: 2px;
      background: linear-gradient(90deg, rgba(99, 102, 241, 0.8), transparent);
    }
  }

  p {
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  min-width: 120px;
`;

const PrimaryButton = styled(Button)`
  background: linear-gradient(
    135deg,
    rgba(99, 102, 241, 0.8) 0%,
    rgba(79, 70, 229, 0.9) 100%
  );
  color: white;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
  }
  
  &:active {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(99, 102, 241, 0.4);
  }
`;

const SecondaryButton = styled(Button)`
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 255, 255, 0.1);
  }
  
  &:active {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(255, 255, 255, 0.1);
  }
`;

const DangerButton = styled(Button)`
  background: rgba(239, 68, 68, 0.1);
  color: rgba(239, 68, 68, 0.9);
  border: 1px solid rgba(239, 68, 68, 0.2);

  &:hover {
    background: rgba(239, 68, 68, 0.2);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
    color: rgba(239, 68, 68, 1);
  }
  
  &:active {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.2);
  }
`;

const ProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1, #3b82f6);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: white;
  box-shadow: 0 4px 10px rgba(99, 102, 241, 0.3);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-top: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoLabel = styled.span`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.span`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
`;

const ScoreDisplay = styled.span`
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  display: inline-block;
  margin-top: 0.25rem;

  &.excellent {
    background-color: rgba(16, 185, 129, 0.3);
    color: rgba(16, 185, 129, 1);
    border: 1px solid rgba(16, 185, 129, 0.5);
  }

  &.good {
    background-color: rgba(59, 130, 246, 0.3);
    color: rgba(59, 130, 246, 1);
    border: 1px solid rgba(59, 130, 246, 0.5);
  }

  &.average {
    background-color: rgba(245, 158, 11, 0.3);
    color: rgba(245, 158, 11, 1);
    border: 1px solid rgba(245, 158, 11, 0.5);
  }

  &.needs-improvement {
    background-color: rgba(239, 68, 68, 0.3);
    color: rgba(239, 68, 68, 1);
    border: 1px solid rgba(239, 68, 68, 0.5);
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${(props) =>
    props.status === "pending"
      ? "rgba(245, 158, 11, 0.8)"
      : props.status === "reviewing"
      ? "rgba(59, 130, 246, 0.8)"
      : props.status === "interview"
      ? "rgba(16, 185, 129, 0.6)"
      : props.status === "accepted"
      ? "rgba(16, 185, 129, 0.8)"
      : props.status === "current intern"
      ? "rgba(5, 150, 105, 0.8)"
      : props.status === "internship complete"
      ? "rgba(79, 70, 229, 0.8)"
      : "rgba(239, 68, 68, 0.8)"};
  color: white;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
`;

const InternDetails = ({ intern, onClose, onEvaluate, onDeleteEvaluation }) => {
  // Function to get initials from name
  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  // Function to get score class
  const getScoreClass = (score) => {
    if (score >= 90) return "excellent";
    if (score >= 75) return "good";
    if (score >= 60) return "average";
    return "needs-improvement";
  };

  const hasEvaluation = intern.evaluation !== undefined;

  return (
    <DetailsContainer>
      <DetailsHeader>
        <DetailsTitle>Intern Details</DetailsTitle>
        <CloseButton onClick={onClose}>&times;</CloseButton>
      </DetailsHeader>

      <ProfileSection>
        <Avatar>{getInitials(intern.studentName)}</Avatar>
        <div>
          <h2 style={{ margin: "0 0 0.5rem 0" }}>{intern.studentName}</h2>
          <p style={{ margin: "0 0 0.25rem 0" }}>{intern.jobTitle}</p>
          <StatusBadge status={intern.status}>
            {intern.status === "current intern" ? "Active" : "Completed"}
          </StatusBadge>
        </div>
      </ProfileSection>

      <DetailsCard>
        <h3>Internship Information</h3>
        <InfoGrid>
          <InfoItem>
            <InfoLabel>University</InfoLabel>
            <InfoValue>{intern.university}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Supervisor</InfoLabel>
            <InfoValue>{intern.supervisor}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Start Date</InfoLabel>
            <InfoValue>{intern.startDate}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>End Date</InfoLabel>
            <InfoValue>{intern.endDate}</InfoValue>
          </InfoItem>
        </InfoGrid>
      </DetailsCard>

      {hasEvaluation ? (
        <DetailsCard>
          <h3>Evaluation</h3>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Technical Skills</InfoLabel>
              <InfoValue>
                <ScoreDisplay className={getScoreClass(intern.evaluation.technical)}>
                  {intern.evaluation.technical}/100
                </ScoreDisplay>
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Communication</InfoLabel>
              <InfoValue>
                <ScoreDisplay className={getScoreClass(intern.evaluation.communication)}>
                  {intern.evaluation.communication}/100
                </ScoreDisplay>
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Problem Solving</InfoLabel>
              <InfoValue>
                <ScoreDisplay className={getScoreClass(intern.evaluation.problemSolving)}>
                  {intern.evaluation.problemSolving}/100
                </ScoreDisplay>
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Teamwork</InfoLabel>
              <InfoValue>
                <ScoreDisplay className={getScoreClass(intern.evaluation.teamwork)}>
                  {intern.evaluation.teamwork}/100
                </ScoreDisplay>
              </InfoValue>
            </InfoItem>
          </InfoGrid>

          <div style={{ 
            marginTop: "2rem", 
            textAlign: "center",
            background: "rgba(255, 255, 255, 0.05)",
            padding: "1.5rem",
            borderRadius: "12px"
          }}>
            <h4 style={{ margin: "0 0 1rem 0" }}>Overall Score</h4>
            <ScoreDisplay 
              className={getScoreClass(intern.evaluation.overall)}
              style={{ 
                fontSize: "1.2rem", 
                padding: "0.5rem 1.5rem"
              }}
            >
              {intern.evaluation.overall}/100
            </ScoreDisplay>
          </div>

          <div style={{ marginTop: "2rem" }}>
            <h4 style={{ 
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
              paddingBottom: "0.5rem"
            }}>Strengths</h4>
            <p style={{ lineHeight: "1.6" }}>{intern.evaluation.strengths}</p>
          </div>

          <div style={{ marginTop: "1.5rem" }}>
            <h4 style={{ 
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
              paddingBottom: "0.5rem"
            }}>Areas for Improvement</h4>
            <p style={{ lineHeight: "1.6" }}>{intern.evaluation.improvements}</p>
          </div>

          <div style={{ marginTop: "1.5rem" }}>
            <h4 style={{ 
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
              paddingBottom: "0.5rem"
            }}>Additional Comments</h4>
            <p style={{ lineHeight: "1.6" }}>{intern.evaluation.comments}</p>
          </div>

          <ButtonContainer style={{ marginTop: "2rem" }}>
            <DangerButton 
              onClick={() => {
                const confirmDelete = window.confirm(`Are you sure you want to delete the evaluation for ${intern.studentName}? This action cannot be undone.`);
                if (confirmDelete) {
                  onDeleteEvaluation(intern);
                }
              }}
            >
              Delete Evaluation
            </DangerButton>
            <PrimaryButton onClick={() => onEvaluate(intern)}>
              Edit Evaluation
            </PrimaryButton>
          </ButtonContainer>
        </DetailsCard>
      ) : (
        intern.status === "internship complete" && (
          <DetailsCard>
            <h3>Evaluation</h3>
            <div style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "1.5rem",
              padding: "2rem",
              textAlign: "center"
            }}>
              <div style={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                background: "rgba(99, 102, 241, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.5rem",
                color: "rgba(99, 102, 241, 0.9)"
              }}>
                📝
              </div>
              <div>
                <h3 style={{ marginBottom: "0.5rem" }}>No Evaluation</h3>
                <p style={{ color: "rgba(255, 255, 255, 0.7)", maxWidth: "400px", margin: "0 auto 1.5rem" }}>
                  This intern hasn't been evaluated yet. Add an evaluation to track their performance.
                </p>
              </div>
              <PrimaryButton onClick={() => onEvaluate(intern)}>
                Submit Evaluation
              </PrimaryButton>
            </div>
          </DetailsCard>
        )
      )}

      <ButtonContainer>
        <SecondaryButton onClick={onClose}>Close</SecondaryButton>
      </ButtonContainer>
    </DetailsContainer>
  );
};

export default InternDetails;
